import React, { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid, TextField, Typography, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { nfcomService } from '../../services/nfcomService';
import EscritorioSelect from '../selects/EscritorioSelect';
import escritorioService from '../../services/escritorioService';
import ClassFiscalSelect from '../selects/ClassFiscalSelect';
// SweetAlert removido: status será exibido diretamente na tabela pela página

interface NfcomGerarLoteModalProps {
  open: boolean;
  onClose: () => void;
  faturaIds: number[];
  totalValor: string; // já formatado para exibição
  onSuccess?: () => void;
  onConfirm?: (params: { data_emissao: string; escritorio_id?: number | null; descricao_template: string; faturaIds: number[]; cclass: string; }) => void | Promise<void>;
}

const NfcomGerarLoteModal: React.FC<NfcomGerarLoteModalProps> = ({ open, onClose, faturaIds, totalValor, onSuccess, onConfirm }) => {
  const getCurrentDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [dataEmissao, setDataEmissao] = useState<string>(getCurrentDate());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [escritorioId, setEscritorioId] = useState<number | null>(null);
  const [descricaoTemplate, setDescricaoTemplate] = useState<'manter_existente' | 'servico_comunicacao_multimidia' | 'servico_comunicacao_multimidia_plano' | 'provimento_internet' | 'titulo_plano'>('manter_existente');  
  const [cclass, setCclass] = useState<string>('0100401');

  const handleEscritorioChange = async (value: number | number[] | null) => {
    const id = (value as number) || null;
    setEscritorioId(id);
    if (typeof id === 'number') {
      try {
        const resp = await escritorioService.getEscritorioById(id);
        const esc: any = (resp as any)?.data || resp;
        if (esc) {
          const desc = esc.descricao_padrao;
          const valid = new Set([
            'manter_existente',
            'servico_comunicacao_multimidia',
            'servico_comunicacao_multimidia_plano',
            'provimento_internet',
            'titulo_plano'
          ]);
          if (typeof desc === 'string' && valid.has(desc) && (desc as string).trim().length > 0) {
            setDescricaoTemplate(desc as any);
          } else {
            setDescricaoTemplate('manter_existente');
          }
        }
      } catch (e) {
        // silenciar erro, campos permanecem como estão
      }
    } else {
      // Limpar campos quando não houver escritório selecionado
      setDescricaoTemplate('manter_existente');
    }
  };

  const qtdeSelecionadas = useMemo(() => faturaIds.length, [faturaIds]);

  const handleGerar = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!dataEmissao) {
        setError('Informe a data de emissão.');
        setLoading(false);
        return;
      }
      if (!escritorioId) {
        setError('Selecione um escritório.');
        setLoading(false);
        return;
      }

      const currDataEmissao = dataEmissao;
      const currEscritorioId = escritorioId;
      const currDescricaoTemplate = descricaoTemplate;
      const currCclass = cclass;

      // Fechar o modal e delegar para a página atualizar a tabela
      onClose();

      if (typeof onConfirm === 'function') {
        await onConfirm({
          data_emissao: currDataEmissao,
          escritorio_id: currEscritorioId,
          descricao_template: currDescricaoTemplate,
          cclass: currCclass,
          faturaIds: faturaIds
        });
      }

      if (onSuccess) onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao gerar notas em lote');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDataEmissao(getCurrentDate());
    setError(null);
    setEscritorioId(null);
    setDescricaoTemplate('manter_existente');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Gerar NFCom em Lote</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <EscritorioSelect
                value={escritorioId}
                onChange={handleEscritorioChange}
                label="Escritório"
                size="small"
                onlyActive
                allowClear
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data de Emissão"
                type="date"
                value={dataEmissao}
                onChange={(e) => setDataEmissao(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={12}>
              <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                <InputLabel>Descrição dos itens</InputLabel>
                <Select
                  value={descricaoTemplate}
                  label="Descrição dos itens"
                  onChange={(e) => setDescricaoTemplate(e.target.value as any)}
                >
                  <MenuItem value={'manter_existente'}>Manter a descrição existente do boleto</MenuItem>
                  <MenuItem value={'servico_comunicacao_multimidia'}>Serviço de Comunicação Multimidia</MenuItem>                  
                  <MenuItem value={'provimento_internet'}>Provimento de Internet</MenuItem>                  
                </Select>
              </FormControl>
            </Grid>

            
            <Grid item xs={12} sm={12}>
              <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                <InputLabel>CClass</InputLabel>
                  <ClassFiscalSelect
                    value={cclass}
                    onChange={(value) => setCclass(value as string)}
                    label="CClass"
                    size="small"
                    fullWidth
                  />
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Box sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
                <Typography variant="caption" color="text.secondary">Qtde. de títulos</Typography>
                <Typography variant="body2"><b>{qtdeSelecionadas}</b></Typography>
              </Box>
            </Grid>
            <Grid item xs={4} sm={8}>
              <Box sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
                <Typography variant="caption" color="text.secondary">Valor total</Typography>
                <Typography variant="body2"><b>{totalValor}</b></Typography>
              </Box>
            </Grid>
          </Grid>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleGerar} disabled={loading || !dataEmissao || qtdeSelecionadas === 0}>Gerar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NfcomGerarLoteModal;


